import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/material";
import { RpcTreeForm } from "../RpcTreeForm";
import { AlgoRpcActions } from "./AlgoRpcActions";
import { NewStrategyDetail } from "./NewStrategyDetail";
import { UseEndpointProvider } from "../../contexts";
import { useSubmitRpcMutation } from "../../services/algoManagementApi";
import { useAlerts } from "@/hooks";
import { useSchemaProcessor } from "@/hooks";
import { getDefaultRpcEntries } from "../../utils/defaultRpcs";
import { computeDefaultFormDataFromBot, normalizeComponents } from "../../utils";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { NEW_STRATEGY_ROW_ID } from "../AlgoTable/AlgoTable.types";
import type { IAGAlgoDataRow } from "../AlgoTable/AlgoTable.types";
import type { InteractionDataByConnection } from "../../utils/filterInteractionRpcs";

export interface AlgoConfigDetailContext {
  interactionsData?: InteractionDataByConnection;
}

export const AlgoConfigDetail = (params: {
  data?: IAGAlgoDataRow;
  context?: AlgoConfigDetailContext;
  api?: any;
}) => {
  const row = params.data;
  const interactionsData = params.context?.interactionsData;
  const closeRow = () => {
    const id = row?.id;
    if (!id || !params.api) return;
    params.api.getRowNode(id)?.setExpanded(false);
  };
  // Branch BEFORE any hooks so the start-new-strategy and edit paths each have
  // their own hook stacks (no rules-of-hooks violation if the row identity flips).
  if (row?.id === NEW_STRATEGY_ROW_ID) {
    return <NewStrategyDetail interactionsData={interactionsData} onSubmitted={closeRow} />;
  }
  return <AlgoConfigDetailExisting row={row} interactionsData={interactionsData} onSubmitted={closeRow} />;
};

const AlgoConfigDetailExisting = ({
  row,
  interactionsData,
  onSubmitted,
}: {
  row?: IAGAlgoDataRow;
  interactionsData?: InteractionDataByConnection;
  onSubmitted?: () => void;
}) => {
  const { showError, showSuccess } = useAlerts();
  const [submitRpc] = useSubmitRpcMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDraft, setCurrentDraft] = useState<any>({});
  const handleDraftChange = useCallback((d: any) => setCurrentDraft(d), []);

  const interaction = row ? interactionsData?.[row.connection_id] : undefined;

  const updateEntry = useMemo(() => {
    if (!interaction) return undefined;
    return getDefaultRpcEntries(interaction).find((e) => e.slot === "update");
  }, [interaction]);

  const schemaWithComponents = useMemo(() => {
    if (!updateEntry || !interaction) return null;
    return { ...updateEntry.schema, components: normalizeComponents(interaction) };
  }, [updateEntry, interaction]);

  const { processedSchema } = useSchemaProcessor(schemaWithComponents);

  const initialData = useMemo(() => {
    if (!processedSchema || !row?.original_data) return {};
    return computeDefaultFormDataFromBot(processedSchema, row.original_data) ?? {};
  }, [processedSchema, row?.original_data]);

  const handleSubmit = useCallback(
    async (args: any) => {
      if (!row || !updateEntry) return;
      setIsSubmitting(true);
      try {
        const body = {
          req_id: uuidv4(),
          connection_id: (interaction as any)?.session_id,
          algo_id: (interaction as any)?.algo_id,
          rpc_name: updateEntry.rpcName,
          args,
        };
        await submitRpc({ data: body }).unwrap();
        showSuccess(`Config updated for ${row.strategy_instance_id}`);
        onSubmitted?.();
      } catch (error: any) {
        showError(`Failed to update config: ${getErrorMessage(error)}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [row, interaction, updateEntry, submitRpc, showSuccess, showError, onSubmitted]
  );

  if (!row || !interaction) {
    return (
      <Box sx={{ p: 2, color: "#888" }}>
        Interaction data not available for this strategy.
      </Box>
    );
  }
  if (!updateEntry) {
    return (
      <Box sx={{ p: 2, color: "#888" }}>
        No update RPC available for this strategy.
      </Box>
    );
  }
  if (!schemaWithComponents) {
    return <Box sx={{ p: 2 }}>Loading…</Box>;
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        borderLeft: "3px solid #1c2e57",
      }}
    >
      <UseEndpointProvider
        interaction={interaction}
        submitRpc={submitRpc}
        showError={showError}
      >
        <RpcTreeForm
          schema={schemaWithComponents}
          initialData={initialData}
          mode="edit"
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onDraftChange={handleDraftChange}
          hideHeaders
          footer={
            <AlgoRpcActions
              interaction={interaction}
              status={row.status}
              algoId={(interaction as any)?.algo_id}
              botId={row.strategy_instance_id}
              onepassToken={typeof currentDraft?.onepass_token === "string" ? currentDraft.onepass_token : undefined}
              onSubmitted={onSubmitted}
            />
          }
        />
      </UseEndpointProvider>
    </Box>
  );
};
